import type { Theme } from '@emotion/react'
import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router'
import { useCommandPaletteKeyboard } from '../hooks/useCommandPaletteKeyboard'
import type { CommandPaletteItem } from '../types'
import { CommandPaletteResultItem } from './CommandPaletteResultItem'

type CommandPaletteResultsListProps = {
  items: CommandPaletteItem[]
  onClose: () => void
}

export const CommandPaletteResultsList = ({
  items,
  onClose,
}: CommandPaletteResultsListProps) => {
  const navigate = useNavigate()
  const itemRefs = useRef<(HTMLDivElement | null)[]>([])

  const handleSelect = (index: number, openInNewTabRequested: boolean) => {
    const item = items[index]
    if (item) {
      if (openInNewTabRequested) {
        window.open(item.path, '_blank')
      } else {
        navigate(item.path)
      }
      onClose()
    }
  }

  const { selectedIndex } = useCommandPaletteKeyboard(
    items.length,
    handleSelect,
    true,
  )

  useEffect(() => {
    const selectedElement = itemRefs.current[selectedIndex]
    if (selectedElement) {
      selectedElement.scrollIntoView({
        block: 'nearest',
        behavior: 'instant',
      })
    }
  }, [selectedIndex])

  return (
    <div
      css={(theme: Theme) => ({
        height: '100%',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: theme.space.Space1,
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
      })}
    >
      {items.map((item, index) => (
        <CommandPaletteResultItem
          key={item.id}
          ref={el => {
            itemRefs.current[index] = el
          }}
          item={item}
          isSelected={index === selectedIndex}
          onClick={openInNewTab => handleSelect(index, openInNewTab)}
          onMiddleClick={() => handleSelect(index, true)}
        />
      ))}
    </div>
  )
}
